/**
 * ChatExamplesManager — manage few-shot SQL examples for an enterprise
 * connector.
 *
 * Embedded panel (not a dialog) with:
 *   - list of saved examples (question + truncated SQL preview in
 *     monospace code block)
 *   - "Aggiungi esempio" inline form (question textarea + SQL textarea)
 *   - per-row delete confirmation via Radix ``AlertDialog``
 *
 * Validation:
 *   - both fields required
 *   - SQL must start with ``SELECT`` (case-insensitive) — anything else
 *     would be rejected by the BE's AST validator anyway
 */

import { msg } from "@lingui/core/macro";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { Loader2, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Skeleton,
} from "@ando/ui";

import { useCreateExample, useDeleteExample, useExamplesQuery } from "../../hooks/useChatExamples";
import { InlineErrorBanner } from "../InlineErrorBanner";

export interface ChatExamplesManagerProps {
  readonly connId: string;
}

function isValidSql(value: string): boolean {
  return /^\s*select\b/i.test(value);
}

function truncate(text: string, max = 140): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}…`;
}

export const ChatExamplesManager: React.FC<ChatExamplesManagerProps> = ({ connId }) => {
  const { i18n, t } = useLingui();
  const examplesQuery = useExamplesQuery(connId);
  const createMutation = useCreateExample();
  const deleteMutation = useDeleteExample();

  const [formOpen, setFormOpen] = React.useState(false);
  const [question, setQuestion] = React.useState("");
  const [sql, setSql] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  const handleCreate = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setError(null);
      const trimmedQuestion = question.trim();
      const trimmedSql = sql.trim();
      if (trimmedQuestion.length === 0 || trimmedSql.length === 0) {
        setError(i18n._(msg`Domanda e SQL sono entrambi obbligatori.`));
        return;
      }
      if (!isValidSql(trimmedSql)) {
        setError(i18n._(msg`La query deve iniziare con SELECT.`));
        return;
      }
      createMutation.mutate(
        {
          connId,
          body: { question: trimmedQuestion, sql_query: trimmedSql, source: "user" },
        },
        {
          onSuccess: () => {
            setQuestion("");
            setSql("");
            setFormOpen(false);
          },
          onError: (err) =>
            setError(err instanceof Error ? err.message : i18n._(msg`Creazione esempio non riuscita.`)),
        },
      );
    },
    [connId, createMutation, question, sql, i18n],
  );

  const handleDelete = React.useCallback(
    (exampleId: string) => {
      setError(null);
      deleteMutation.mutate(
        { connId, exampleId },
        {
          onSettled: () => setDeletingId(null),
          onError: (err) =>
            setError(err instanceof Error ? err.message : i18n._(msg`Eliminazione non riuscita.`)),
        },
      );
    },
    [connId, deleteMutation, i18n],
  );

  const examples = examplesQuery.data ?? [];

  if (examplesQuery.isLoading) {
    return (
      <div className="rounded-lg border border-hairline bg-card/60 p-4" aria-busy>
        <Skeleton className="h-5 w-48" />
        <Skeleton className="mt-3 h-12 w-full" />
        <Skeleton className="mt-2 h-12 w-full" />
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-hairline bg-card/60 p-4">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display font-semibold text-[14px] text-ink-1"><Trans>Esempi few-shot</Trans></h3>
          <p className="mt-0.5 text-[11.5px] text-ink-3">
            <Trans>Domande tipiche con la SQL corretta — usate per guidare il text-to-SQL.</Trans>
          </p>
        </div>
        {!formOpen ? (
          <Button type="button" variant="primary" size="sm" onClick={() => setFormOpen(true)}>
            <Plus className="h-3 w-3" aria-hidden />
            <Trans>Aggiungi esempio</Trans>
          </Button>
        ) : null}
      </header>

      <InlineErrorBanner message={error} />

      {/* ── Add form ──────────────────────────────────────────────── */}
      {formOpen ? (
        <form
          onSubmit={handleCreate}
          className="flex flex-col gap-3 rounded-md border border-hairline bg-white/40 p-3"
        >
          <div className="flex flex-col gap-1">
            <label htmlFor="cem-question" className="font-medium text-[11.5px] text-ink-2">
              <Trans>Domanda</Trans>
              <span className="ml-0.5 text-error" aria-hidden>
                *
              </span>
            </label>
            <textarea
              id="cem-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={2}
              placeholder={i18n._(msg`Es: Mostra il fatturato per cliente nel 2026.`)}
              required
              disabled={createMutation.isPending}
              className="w-full resize-y rounded-md border border-hairline bg-white/80 px-2 py-1.5 text-[12.5px] text-ink-1 placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="cem-sql" className="font-medium text-[11.5px] text-ink-2">
              SQL
              <span className="ml-0.5 text-error" aria-hidden>
                *
              </span>
            </label>
            <textarea
              id="cem-sql"
              value={sql}
              onChange={(e) => setSql(e.target.value)}
              rows={4}
              placeholder="SELECT cliente, SUM(importo) FROM fatture WHERE anno = 2026 GROUP BY cliente;"
              required
              disabled={createMutation.isPending}
              className="w-full resize-y rounded-md border border-hairline bg-white/80 px-2 py-1.5 font-mono text-[11.5px] text-ink-1 placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
            />
            <p className="text-[11px] text-ink-3">
              <Trans>Verrà usato come few-shot per migliorare la generazione SQL.</Trans>
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setFormOpen(false);
                setQuestion("");
                setSql("");
                setError(null);
              }}
              disabled={createMutation.isPending}
            >
              <Trans>Annulla</Trans>
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : null}
              <Trans>Salva esempio</Trans>
            </Button>
          </div>
        </form>
      ) : null}

      {/* ── List ──────────────────────────────────────────────────── */}
      {examples.length === 0 ? (
        <div className="rounded-md border border-hairline border-dashed bg-cream/40 p-4 text-center">
          <p className="text-[12.5px] text-ink-2">
            <Trans>
              Nessun esempio configurato. Aggiungi domande tipiche con la SQL corretta per migliorare
              il text-to-SQL.
            </Trans>
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {examples.map((ex) => (
            <li
              key={ex.id}
              className="flex items-start gap-3 rounded-md border border-hairline bg-white/40 p-3"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <p className="font-medium text-[12.5px] text-ink-1">{ex.question}</p>
                <pre className="overflow-x-auto rounded bg-ink-1/[0.04] px-2 py-1.5 font-mono text-[11px] text-ink-2">
                  <code>{truncate(ex.sql_query)}</code>
                </pre>
                {ex.used_count > 0 ? (
                  <span className="text-[10.5px] text-ink-3">
                    <Plural
                      value={ex.used_count}
                      one="Usato # volta"
                      other="Usato # volte"
                    />
                  </span>
                ) : null}
              </div>
              <AlertDialog
                open={deletingId === ex.id}
                onOpenChange={(o) => (o ? setDeletingId(ex.id) : setDeletingId(null))}
              >
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    aria-label={t`Elimina esempio: ${ex.question}`}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-3 transition-colors hover:bg-error/[0.08] hover:text-error focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle><Trans>Eliminare l'esempio?</Trans></AlertDialogTitle>
                    <AlertDialogDescription>
                      <Trans>L'esempio non sarà più usato per guidare il text-to-SQL.</Trans>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel><Trans>Annulla</Trans></AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleDelete(ex.id)}>
                      <Trans>Elimina</Trans>
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
ChatExamplesManager.displayName = "ChatExamplesManager";
